import {
  createAccount,
  finishSignIn,
  Forbidden,
  InvalidInput,
  NotFound,
  passkeySignIn,
  passkeySignInOptions,
  passkeysOffered,
  signIn,
  type SignInStep,
  type User,
} from '@trippath/shared';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { CodeField } from '../../components/security/CodeField';
import { problemKey } from '../../lib/format';
import { passkeysHere, signWithPasskey } from '../../lib/passkeys';

interface Props {
  /** The server has no account yet: this page makes the first one. */
  first: boolean;
  onSignedIn: (user: User) => void;
}

interface FieldProps {
  id: string;
  label: string;
  type: 'text' | 'email' | 'password';
  autoComplete: string;
  value: string;
  error: string | undefined;
  onChange: (value: string) => void;
}

export function Field({ id, label, type, autoComplete, value, error, onChange }: FieldProps) {
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="text-input"
        type={type}
        autoComplete={autoComplete}
        value={value}
        aria-invalid={error !== undefined}
        aria-describedby={error === undefined ? undefined : `${id}-error`}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
      {error !== undefined && (
        <p className="field-error" id={`${id}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}

/** A passkey's sign-in: both steps at once. */
function PasskeyButton({
  onSignedIn,
  onFailed,
}: {
  onSignedIn: (user: User) => void;
  onFailed: (message: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className="button"
      onClick={() => {
        passkeySignInOptions()
          .then(signWithPasskey)
          .then(passkeySignIn)
          .then(onSignedIn, (error: unknown) => {
            onFailed(
              t(
                error instanceof InvalidInput
                  ? 'security.passkeyWrong'
                  : 'security.passkeyCancelled',
              ),
            );
          });
      }}
    >
      {t('security.usePasskey')}
    </button>
  );
}

/** The second step of a sign-in: the app's code, or a backup code; the device trusted or not. */
function SecondStep({
  ticket,
  passkeys,
  onSignedIn,
  onGone,
}: {
  ticket: string;
  passkeys: boolean;
  onSignedIn: (user: User) => void;
  onGone: () => void;
}) {
  const { t } = useTranslation();
  const ids = useId();
  const [backup, setBackup] = useState(false);
  const [code, setCode] = useState('');
  const [trust, setTrust] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [failed, setFailed] = useState<string | null>(null);
  return (
    <form
      className="panel form second-step"
      noValidate
      aria-labelledby={`${ids}-title`}
      onSubmit={(event) => {
        event.preventDefault();
        setError(undefined);
        setFailed(null);
        finishSignIn(ticket, code, trust).then(onSignedIn, (problem: unknown) => {
          if (problem instanceof InvalidInput) setError(t('security.wrongCode'));
          else if (problem instanceof NotFound) onGone();
          else setFailed(t('trips.failed'));
        });
      }}
    >
      <h1 id={`${ids}-title`}>{t(backup ? 'security.backupTitle' : 'security.secondTitle')}</h1>
      {backup ? (
        <Field
          id={`${ids}-backup`}
          label={t('security.backupCode')}
          type="text"
          autoComplete="off"
          value={code}
          error={error}
          onChange={setCode}
        />
      ) : (
        <CodeField
          id={`${ids}-code`}
          label={t('security.code')}
          value={code}
          error={error}
          onChange={setCode}
        />
      )}
      <label className="check">
        <input
          type="checkbox"
          checked={trust}
          onChange={(event) => {
            setTrust(event.target.checked);
          }}
        />
        {t('security.trust')}
      </label>
      {failed !== null && (
        <p className="form-failed" role="alert">
          {failed}
        </p>
      )}
      <button type="submit" className="button button-primary">
        {t('account.signIn')}
      </button>
      {passkeys && (
        <>
          <div className="or">{t('security.or')}</div>
          <PasskeyButton onSignedIn={onSignedIn} onFailed={setFailed} />
        </>
      )}
      <button
        type="button"
        className="link-button second-step-switch"
        onClick={() => {
          setBackup(!backup);
          setCode('');
          setError(undefined);
        }}
      >
        {t(backup ? 'security.useApp' : 'security.useBackup')}
      </button>
    </form>
  );
}

/**
 * Signing in, or on a server with no account yet, making the first one — which becomes the
 * owner of every trip made before there were accounts.
 */
export function SignIn({ first, onSignedIn }: Props) {
  const { t, i18n } = useTranslation();
  const ids = useId();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState(false);
  const [ticket, setTicket] = useState<string | null>(null);
  const [passkeys, setPasskeys] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => {
    if (first) return;
    passkeysOffered().then(
      (offered) => {
        setPasskeys(offered && passkeysHere());
      },
      () => undefined,
    );
  }, [first]);

  const submit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    setErrors({});
    setFailed(false);
    setNote(null);
    const job = first
      ? createAccount({ email, name, password, language: i18n.language }).then(
          (user): SignInStep => ({ kind: 'in', user }),
        )
      : signIn(email, password);
    job.then(
      (step) => {
        if (step.kind === 'in') onSignedIn(step.user);
        else setTicket(step.ticket);
      },
      (error: unknown) => {
        if (error instanceof InvalidInput) {
          setErrors(
            Object.fromEntries(
              error.fields.map((f) => [
                f.field,
                f.field === 'password' && f.problem === 'mismatch'
                  ? t('account.wrong')
                  : t(problemKey(f.problem)),
              ]),
            ),
          );
        } else if (error instanceof Forbidden) {
          setNote(t('account.turnedOff'));
        } else {
          setFailed(true);
        }
      },
    );
  };

  if (ticket !== null) {
    return (
      <div className="page sign-in">
        <SecondStep
          ticket={ticket}
          passkeys={passkeys}
          onSignedIn={onSignedIn}
          onGone={() => {
            setTicket(null);
            setPassword('');
            setNote(t('security.ticketGone'));
          }}
        />
      </div>
    );
  }

  return (
    <div className="page sign-in">
      <form
        className="panel form"
        onSubmit={submit}
        noValidate
        aria-labelledby={`${ids}-title`}
      >
        <h1 id={`${ids}-title`}>{t(first ? 'account.firstTitle' : 'account.title')}</h1>
        {first && <p className="quiet">{t('account.firstLead')}</p>}
        {first && (
          <Field
            id={`${ids}-name`}
            label={t('account.name')}
            type="text"
            autoComplete="name"
            value={name}
            error={errors.name}
            onChange={setName}
          />
        )}
        <Field
          id={`${ids}-email`}
          label={t('account.email')}
          type="email"
          autoComplete="email"
          value={email}
          error={errors.email}
          onChange={setEmail}
        />
        <Field
          id={`${ids}-password`}
          label={t('account.password')}
          type="password"
          autoComplete={first ? 'new-password' : 'current-password'}
          value={password}
          error={errors.password}
          onChange={setPassword}
        />
        {first && <p className="quiet">{t('account.passwordHint')}</p>}
        {failed && (
          <p className="form-failed" role="alert">
            {t('trips.failed')}
          </p>
        )}
        {note !== null && (
          <p className="form-failed" role="alert">
            {note}
          </p>
        )}
        <button type="submit" className="button button-primary">
          {t(first ? 'account.create' : 'account.signIn')}
        </button>
        {!first && (
          <Link className="link-button" to="/forgot">
            {t('reset.forgot')}
          </Link>
        )}
        {passkeys && (
          <>
            <div className="or">{t('security.or')}</div>
            <PasskeyButton onSignedIn={onSignedIn} onFailed={setNote} />
          </>
        )}
      </form>
    </div>
  );
}
